import { Template } from '../../mocks/websiteTemplates';

export const CATEGORY_SLUGS: Record<string, string> = {
  'E-commerce': 'e-commerce',
  'SaaS & Tech': 'saas-tech',
  'Portfolio & Creative': 'portfolio-creative',
  'Restaurant & Food': 'restaurant-food',
  'Health & Wellness': 'health-wellness',
  'Real Estate': 'real-estate',
  'Education': 'education',
  'Non-Profit': 'non-profit',
  'Fashion & Beauty': 'fashion-beauty',
  'Travel & Hospitality': 'travel-hospitality',
  'Finance & Legal': 'finance-legal',
  'Entertainment & Media': 'entertainment-media',
};

export const SLUG_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_SLUGS).map(([k, v]) => [v, k])
);

export interface CategoryMeta {
  icon: string;
  gradient: string;
  gradientFrom: string;
  gradientTo: string;
  lightBg: string;
  description: string;
  tagline: string;
  bgImage: string;
}

export const CATEGORY_META: Record<string, CategoryMeta> = {
  'E-commerce': {
    icon: 'ri-shopping-bag-3-line',
    gradient: 'from-emerald-600 to-teal-700',
    gradientFrom: '#059669',
    gradientTo: '#0f766e',
    lightBg: 'from-emerald-50 to-teal-50',
    tagline: 'Sell More, Everywhere',
    description: 'Build powerful online stores that convert browsers into buyers. From fashion boutiques to electronics marketplaces, our e-commerce templates are engineered to drive sales and delight shoppers at every touchpoint.',
    bgImage: 'https://public.readdy.ai/ai/img_res/837345fac3e276c3e9da2ce14bfe3155.jpg',
  },
  'SaaS & Tech': {
    icon: 'ri-code-box-line',
    gradient: 'from-violet-700 to-indigo-800',
    gradientFrom: '#6d28d9',
    gradientTo: '#3730a3',
    lightBg: 'from-violet-50 to-indigo-50',
    tagline: 'Ship Faster, Convert Better',
    description: 'Launch your SaaS product with a landing page that turns visitors into paying customers. Clean, modern tech templates built with conversion rate optimization baked in from the ground up.',
    bgImage: 'https://readdy.ai/api/search-image?query=saas%20software%20technology%20abstract%20background%20dark%20violet%20indigo%20glow%20code%20dashboard%20interface%20lines%20minimal%20futuristic%20professional%20photography&width=1400&height=500&seq=cat-hero-st&orientation=landscape',
  },
  'Portfolio & Creative': {
    icon: 'ri-palette-line',
    gradient: 'from-pink-600 to-rose-700',
    gradientFrom: '#db2777',
    gradientTo: '#be123c',
    lightBg: 'from-pink-50 to-rose-50',
    tagline: 'Your Work, Front and Center',
    description: 'Showcase your creative work with portfolios that make clients stop scrolling. Carefully crafted for designers, photographers, illustrators, architects and every creative professional who deserves to be noticed.',
    bgImage: 'https://readdy.ai/api/search-image?query=creative%20portfolio%20design%20art%20abstract%20background%20soft%20pink%20rose%20gradient%20artistic%20photography%20studio%20minimal%20clean%20professional&width=1400&height=500&seq=cat-hero-pc&orientation=landscape',
  },
  'Restaurant & Food': {
    icon: 'ri-restaurant-line',
    gradient: 'from-orange-600 to-amber-700',
    gradientFrom: '#ea580c',
    gradientTo: '#b45309',
    lightBg: 'from-orange-50 to-amber-50',
    tagline: 'Fill Tables, Delight Guests',
    description: 'Tempt appetites and drive reservations with gorgeous food and restaurant websites. From Michelin-starred fine dining to cozy neighborhood cafes, find the perfect design to represent your culinary identity.',
    bgImage: 'https://readdy.ai/api/search-image?query=restaurant%20food%20dining%20beautiful%20abstract%20warm%20background%20orange%20amber%20atmospheric%20bokeh%20culinary%20photography%20professional%20moody%20lighting&width=1400&height=500&seq=cat-hero-rf&orientation=landscape',
  },
  'Health & Wellness': {
    icon: 'ri-heart-pulse-line',
    gradient: 'from-green-600 to-emerald-700',
    gradientFrom: '#16a34a',
    gradientTo: '#047857',
    lightBg: 'from-green-50 to-emerald-50',
    tagline: 'Inspire Healthier Lives',
    description: 'Build trust and inspire action with calm, professional wellness websites. Ideal for clinics, yoga studios, personal trainers, therapists, and wellness coaches who want their online presence to reflect their care.',
    bgImage: 'https://readdy.ai/api/search-image?query=health%20wellness%20yoga%20spa%20abstract%20background%20serene%20calm%20green%20nature%20soft%20bokeh%20professional%20photography%20mindfulness%20peaceful&width=1400&height=500&seq=cat-hero-hw&orientation=landscape',
  },
  'Real Estate': {
    icon: 'ri-building-line',
    gradient: 'from-cyan-600 to-sky-700',
    gradientFrom: '#0891b2',
    gradientTo: '#0369a1',
    lightBg: 'from-cyan-50 to-sky-50',
    tagline: 'List, Show, Close',
    description: 'Close more deals with property websites that make listings impossible to ignore. Clean, professional templates for real estate agents, agencies, developers and property managers who mean business.',
    bgImage: 'https://readdy.ai/api/search-image?query=real%20estate%20property%20luxury%20architecture%20abstract%20background%20cyan%20blue%20sky%20modern%20building%20aerial%20photography%20professional%20clean&width=1400&height=500&seq=cat-hero-re&orientation=landscape',
  },
  'Education': {
    icon: 'ri-graduation-cap-line',
    gradient: 'from-amber-600 to-yellow-700',
    gradientFrom: '#d97706',
    gradientTo: '#b45309',
    lightBg: 'from-amber-50 to-yellow-50',
    tagline: 'Teach, Inspire, Transform',
    description: 'Empower learners and educators with beautiful, functional education websites. From online course platforms to universities and tutoring services, we have a design for every teaching mission.',
    bgImage: 'https://readdy.ai/api/search-image?query=education%20learning%20knowledge%20abstract%20background%20amber%20golden%20warm%20books%20academic%20university%20professional%20photography%20studio&width=1400&height=500&seq=cat-hero-ed&orientation=landscape',
  },
  'Non-Profit': {
    icon: 'ri-hearts-line',
    gradient: 'from-red-600 to-rose-700',
    gradientFrom: '#dc2626',
    gradientTo: '#be123c',
    lightBg: 'from-red-50 to-rose-50',
    tagline: 'Tell Your Story, Drive Impact',
    description: 'Amplify your mission with non-profit websites that move people to act, donate, and volunteer. Purpose-built templates for charities, foundations, community organizations and advocacy groups.',
    bgImage: 'https://readdy.ai/api/search-image?query=non-profit%20charity%20community%20abstract%20background%20warm%20red%20rose%20heart%20humanity%20professional%20photography%20hope%20inspiration&width=1400&height=500&seq=cat-hero-np&orientation=landscape',
  },
  'Fashion & Beauty': {
    icon: 'ri-scissors-line',
    gradient: 'from-fuchsia-600 to-pink-700',
    gradientFrom: '#c026d3',
    gradientTo: '#be185d',
    lightBg: 'from-fuchsia-50 to-pink-50',
    tagline: 'Look Good, Feel Good Online',
    description: 'Turn heads online with fashion-forward beauty websites that are as stunning as your work. Elegant templates for hair salons, makeup artists, clothing brands, beauty influencers and boutiques.',
    bgImage: 'https://readdy.ai/api/search-image?query=fashion%20beauty%20luxury%20elegant%20abstract%20background%20fuchsia%20pink%20glamour%20professional%20photography%20editorial%20style%20minimal%20clean&width=1400&height=500&seq=cat-hero-fb&orientation=landscape',
  },
  'Travel & Hospitality': {
    icon: 'ri-plane-line',
    gradient: 'from-sky-600 to-cyan-700',
    gradientFrom: '#0284c7',
    gradientTo: '#0e7490',
    lightBg: 'from-sky-50 to-cyan-50',
    tagline: 'Adventure Awaits',
    description: 'Fuel wanderlust and fill bookings with stunning travel and hospitality websites. From boutique hotels and resorts to adventure tour operators and travel bloggers — create your digital destination today.',
    bgImage: 'https://readdy.ai/api/search-image?query=travel%20hospitality%20destination%20adventure%20abstract%20background%20sky%20blue%20aerial%20wanderlust%20professional%20photography%20tropical%20paradise&width=1400&height=500&seq=cat-hero-th&orientation=landscape',
  },
  'Finance & Legal': {
    icon: 'ri-scales-3-line',
    gradient: 'from-slate-700 to-gray-800',
    gradientFrom: '#334155',
    gradientTo: '#1f2937',
    lightBg: 'from-slate-50 to-gray-100',
    tagline: 'Authority You Can Trust',
    description: 'Build authority and credibility with professional finance and legal websites. Clean, sophisticated templates for law firms, financial advisors, accountants and fintech companies who command respect.',
    bgImage: 'https://readdy.ai/api/search-image?query=finance%20legal%20corporate%20professional%20abstract%20background%20slate%20dark%20grey%20authority%20trust%20clean%20minimal%20photography%20building%20architecture&width=1400&height=500&seq=cat-hero-fl&orientation=landscape',
  },
  'Entertainment & Media': {
    icon: 'ri-film-line',
    gradient: 'from-purple-700 to-violet-800',
    gradientFrom: '#7c3aed',
    gradientTo: '#5b21b6',
    lightBg: 'from-purple-50 to-violet-50',
    tagline: 'Entertain. Engage. Grow.',
    description: 'Capture attention and grow your audience with dynamic entertainment and media websites. Perfect for musicians, podcasters, film studios, esports organizations, event venues and content creators.',
    bgImage: 'https://readdy.ai/api/search-image?query=entertainment%20media%20music%20film%20abstract%20background%20dark%20purple%20violet%20neon%20glow%20dynamic%20professional%20photography%20cinematic%20atmospheric&width=1400&height=500&seq=cat-hero-em&orientation=landscape',
  },
};

export const nameToSlug = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const slugToTemplate = (slug: string, templates: Template[]): Template | undefined =>
  templates.find((t) => nameToSlug(t.name) === slug);

// ── AI Semantic Search ──────────────────────────────────────────────────────

interface SemanticMapping {
  keywords: string[];
  tags?: string[];
  categories?: string[];
  weight: number;
}

const SEMANTIC_MAPPINGS: SemanticMapping[] = [
  { keywords: ['dark', 'moody', 'night', 'black', 'shadow', 'noir'], tags: ['Dark'], weight: 3 },
  { keywords: ['minimal', 'clean', 'simple', 'pure', 'stripped', 'bare'], tags: ['Minimal', 'Clean'], weight: 3 },
  { keywords: ['bold', 'strong', 'striking', 'powerful', 'loud'], tags: ['Bold'], weight: 2 },
  { keywords: ['colorful', 'vibrant', 'bright', 'vivid', 'rainbow'], tags: ['Colorful'], weight: 2 },
  { keywords: ['warm', 'cozy', 'friendly', 'inviting', 'earthy'], tags: ['Warm'], weight: 2 },
  { keywords: ['elegant', 'luxury', 'premium', 'sophisticated', 'refined', 'upscale'], tags: ['Elegant'], weight: 3 },
  { keywords: ['modern', 'contemporary', 'trendy', 'current', 'fresh'], tags: ['Modern'], weight: 2 },
  { keywords: ['corporate', 'professional', 'formal', 'enterprise', 'b2b'], tags: ['Corporate'], weight: 2 },
  { keywords: ['creative', 'artistic', 'unique', 'expressive'], tags: ['Creative'], weight: 2 },
  { keywords: ['shop', 'store', 'ecommerce', 'sell', 'merchandise', 'retail', 'buy', 'cart', 'product', 'commerce'], categories: ['E-commerce'], weight: 4 },
  { keywords: ['saas', 'software', 'app', 'tech', 'startup', 'platform', 'dashboard', 'api', 'developer', 'tool'], categories: ['SaaS & Tech'], weight: 4 },
  { keywords: ['portfolio', 'photographer', 'designer', 'artist', 'gallery', 'showcase', 'illustrator', 'work', 'folio'], categories: ['Portfolio & Creative'], weight: 4 },
  { keywords: ['restaurant', 'food', 'cafe', 'bar', 'menu', 'dining', 'eat', 'drink', 'coffee', 'pizza', 'chef', 'catering', 'kitchen'], categories: ['Restaurant & Food'], weight: 4 },
  { keywords: ['health', 'wellness', 'gym', 'fitness', 'yoga', 'medical', 'clinic', 'doctor', 'therapy', 'spa', 'pilates'], categories: ['Health & Wellness'], weight: 4 },
  { keywords: ['real estate', 'property', 'house', 'apartment', 'rent', 'realtor', 'mortgage', 'realty', 'housing', 'estate'], categories: ['Real Estate'], weight: 4 },
  { keywords: ['education', 'school', 'university', 'course', 'learn', 'teach', 'training', 'tutorial', 'bootcamp', 'study'], categories: ['Education'], weight: 4 },
  { keywords: ['charity', 'nonprofit', 'donate', 'volunteer', 'cause', 'foundation', 'ngo', 'community'], categories: ['Non-Profit'], weight: 4 },
  { keywords: ['fashion', 'beauty', 'style', 'makeup', 'hair', 'clothing', 'salon', 'boutique', 'brand', 'wardrobe'], categories: ['Fashion & Beauty'], weight: 4 },
  { keywords: ['travel', 'hotel', 'vacation', 'tourism', 'trip', 'booking', 'resort', 'destination', 'flight', 'airbnb'], categories: ['Travel & Hospitality'], weight: 4 },
  { keywords: ['finance', 'legal', 'law', 'lawyer', 'attorney', 'accounting', 'bank', 'investment', 'fintech', 'advisor'], categories: ['Finance & Legal'], weight: 4 },
  { keywords: ['music', 'film', 'movie', 'gaming', 'entertainment', 'event', 'concert', 'media', 'podcast', 'band', 'streaming'], categories: ['Entertainment & Media'], weight: 4 },
];

export function semanticScore(template: Template, query: string): number {
  const q = query.toLowerCase();
  let score = 0;

  for (const mapping of SEMANTIC_MAPPINGS) {
    const hit = mapping.keywords.find((kw) => q.includes(kw));
    if (hit) {
      if (mapping.tags) {
        const tagHit = mapping.tags.some((tag) => template.tags.includes(tag));
        if (tagHit) score += mapping.weight;
      }
      if (mapping.categories) {
        const catHit = mapping.categories.includes(template.category);
        if (catHit) score += mapping.weight;
      }
    }
  }

  // Direct word matches in name/description (lower weight)
  const words = q.split(/\s+/).filter((w) => w.length > 2);
  words.forEach((w) => {
    if (template.name.toLowerCase().includes(w)) score += 1.5;
    if (template.description.toLowerCase().includes(w)) score += 0.5;
  });

  return score;
}
